import { DownloadOutlined } from "@mui/icons-material";
import { Card, CardContent, CardHeader, Divider } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Button, Typography } from "@platform/design-system";
import React, { useState } from "react";
import { useApi } from "@platform/api";

import FileCard from "../../../components/fileCard";
import useFormatFiles from "../../../hooks/useFormatFiles";
import { Coverage } from "../../../types/rfpApi";
import { downloadBinaryFile, extractErrorMessage } from "../../../utils";
import { RfpFile } from "../types";

type OriginalFile = {
  id: string;
  name: string;
  downloadLink: string;
};

type DisplayFile = {
  id: string | number;
  original: OriginalFile;
  productsByType: Record<string, string[]>;
};

type Props = {
  files?: RfpFile[];
  coverages?: Coverage[];
  additionalDocs?: string[];
};

const NewDocumentInformation = ({
  files,
  coverages,
  additionalDocs,
}: Props): React.JSX.Element => {
  const [downloadingId, setDownloadingId] = useState<string>("");
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [error, setError] = useState<{ id: string; msg: string } | null>(null);
  const axiosInstance = useApi();

  const handleFileDownload = async (file: OriginalFile): Promise<void> => {
    setDownloadingId(file.id);
    if (error && error.id === file.id) {
      setError(null);
    }

    try {
      const response = await axiosInstance.post(
        `brokerportal/v1/getRfpFile`,
        { downloadLink: file.downloadLink },
        {
          responseType: "blob",
        },
      );
      const data = response.data as Blob;
      downloadBinaryFile(data, file.name);
    } catch (err: unknown) {
      setError({ id: file.id, msg: extractErrorMessage(err) as string });
    } finally {
      setDownloadingId("");
    }
  };

  const handleDownloadFileList = async (list: DisplayFile[]): Promise<void> => {
    setButtonLoading(true);
    try {
      await Promise.allSettled(
        list.map((file) => handleFileDownload(file.original)),
      );
    } finally {
      setButtonLoading(false);
    }
  };

  const newFiles = useFormatFiles(
    files as RfpFile[] | null,
    coverages as Coverage[],
    additionalDocs as string[],
  ) as DisplayFile[] | null;

  return (
    <DocumentCard
      newFiles={newFiles}
      buttonLoading={buttonLoading}
      handleDownloadFileList={handleDownloadFileList}
      downloadingId={downloadingId}
      error={error}
      handleFileDownload={handleFileDownload}
    />
  );
};

type DocumentCardProps = {
  newFiles: DisplayFile[] | null;
  buttonLoading: boolean;
  handleDownloadFileList: (list: DisplayFile[]) => Promise<void>;
  downloadingId: string;
  error: { id: string; msg: string } | null;
  handleFileDownload: (file: OriginalFile) => Promise<void>;
};

const DocumentCard = ({
  newFiles,
  buttonLoading,
  handleDownloadFileList,
  downloadingId,
  error,
  handleFileDownload,
}: DocumentCardProps): React.JSX.Element => (
  <Card>
    <CardHeader
      title={
        <Typography variant="header-medium">Uploaded Documents</Typography>
      }
      action={
        newFiles && newFiles.length > 0 ? (
          <Button
            variant="secondary"
            startIcon={<DownloadOutlined />}
            onClick={() => handleDownloadFileList(newFiles)}
            disabled={buttonLoading}
          >
            EXPORT DOCUMENTS
          </Button>
        ) : null
      }
    />
    <Divider />
    <CardContent>
      <Grid container spacing={2}>
        {!newFiles || newFiles.length === 0 ? (
          <Grid size={12}>
            <Typography variant="body-medium" color="textSecondary">
              <strong>No documents uploaded.</strong>
            </Typography>
          </Grid>
        ) : (
          newFiles.map(
            (file): React.JSX.Element => (
              <Grid key={`new-files-${file.id}`} size={6}>
                <FileCard
                  key={file.id}
                  file={{
                    ...file,
                    id: String(file.id),
                  }}
                  loading={downloadingId}
                  error={error}
                  onFileDownload={() => handleFileDownload(file.original)}
                />
              </Grid>
            ),
          )
        )}
      </Grid>
    </CardContent>
  </Card>
);

export default NewDocumentInformation;
